import axios from 'axios';

export interface CodeReviewScore {
  correctness: number;
  readability: number;
  efficiency: number;
  bestPractices: number;
  maintainability: number;
  documentation: number;
  security: number;
  overall: number;
  descriptions: Record<string, string>;
  recommendations: string[];
  summary: string;
}

export class CodeReviewService {
  private readonly apiKey = "qx1jpBjkZCFZ1pghuGJRUot42B0KcfTF";
  private readonly mistralApiUrl = "https://api.mistral.ai/v1/chat/completions";

  async getCodeReview(code: string): Promise<CodeReviewScore> {
    try {
      const messages = [
        {
          role: "system",
          content: `
          You are a highly skilled code quality assistant integrated into AlgoFlow. Your task is to thoroughly review the provided code and deliver a detailed, actionable, and constructive analysis. Follow these guidelines:

          1. **Evaluation Criteria**:
             - Correctness: Does the code function as intended? Identify logical errors, edge cases, or bugs.
             - Readability: Is the code easy to understand? Assess naming conventions, structure, and clarity.
             - Efficiency: Are there performance bottlenecks? Suggest optimizations for time and space complexity.
             - Best Practices: Does the code follow industry standards? Check for adherence to SOLID principles, DRY, and clean code practices.
             - Maintainability: Is the code modular and easy to extend? Evaluate separation of concerns and scalability.
             - Documentation: Are comments and documentation sufficient? Highlight areas needing clarification.
             - Security: Are there potential vulnerabilities? Identify risks like SQL injection, XSS, or insecure API usage.

          2. **Output Format**:
             - Provide a summary of the code's strengths and weaknesses.
             - Assign a score (0-100%) for each criterion.
             - Include a **single-line description** for each score to explain why it was given.
             - Include an overall score (0-100%).
             - Provide actionable recommendations for improvement.
             - For each recommendation, explicitly state what to add, change, or improve in the code. Use clear phrases like "Add this to your code:", "Change this part like this:", or "Improve by doing this:" followed by a markdown code block (triple backticks, specify language if possible) with a code snippet or pseudocode.
             - Use instructional language such as "Write code like this...", "Add this function...", "Replace with this...", "Include this check...", etc.
             - Format the response as follows:

             \`\`\`
             Summary:
             <A brief summary of the code's strengths and weaknesses.>

             Scores:
             - Correctness: <score>% - <single-line description>
             - Readability: <score>% - <single-line description>
             - Efficiency: <score>% - <single-line description>
             - Best Practices: <score>% - <single-line description>
             - Maintainability: <score>% - <single-line description>
             - Documentation: <score>% - <single-line description>
             - Security: <score>% - <single-line description>

             Overall Score: <overall_score>%

             Recommendations:
             <numbered list of recommendations. For each, clearly state what to add/change/improve, then provide a markdown code block with an example.>

             - Be concise and professional.
             - Do not include unnecessary repetition or filler text.
          `
        },
        {
          role: "user",
          content: `Please review the following code:\n\n${code}`
        }
      ];

      const requestBody = {
        model: "open-codestral-mamba",
        messages,
        temperature: 0.1, // more deterministic output
        max_tokens: 500
      };

      const response = await axios.post(
        this.mistralApiUrl,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const aiResponse = response.data.choices[0].message.content;
      
      // Parse the response to extract scores and recommendations
      return this.parseCodeReviewResponse(aiResponse);
    } catch (error) {
      console.error('Error during API call:', error);
      throw new Error('Failed to get code review');
    }
  }

  private parseCodeReviewResponse(responseText: string): CodeReviewScore {
    const result: CodeReviewScore = {
      correctness: 0,
      readability: 0,
      efficiency: 0,
      bestPractices: 0,
      maintainability: 0,
      documentation: 0,
      security: 0,
      overall: 0,
      descriptions: {},
      recommendations: [],
      summary: ''
    };

    // Extract summary - use a workaround instead of 's' flag
    const summaryRegex = /Summary:\s*([\s\S]*?)(?:\n\s*\n|\nScores:)/;
    const summaryMatch = responseText.match(summaryRegex);
    if (summaryMatch) {
      result.summary = summaryMatch[1].trim();
    }

    // Extract scores
    const correctnessMatch = responseText.match(/Correctness:\s*(\d+)%\s*-\s*([^\n]+)/);
    if (correctnessMatch) {
      result.correctness = parseInt(correctnessMatch[1]);
      result.descriptions['correctness'] = correctnessMatch[2].trim();
    }

    const readabilityMatch = responseText.match(/Readability:\s*(\d+)%\s*-\s*([^\n]+)/);
    if (readabilityMatch) {
      result.readability = parseInt(readabilityMatch[1]);
      result.descriptions['readability'] = readabilityMatch[2].trim();
    }

    const efficiencyMatch = responseText.match(/Efficiency:\s*(\d+)%\s*-\s*([^\n]+)/);
    if (efficiencyMatch) {
      result.efficiency = parseInt(efficiencyMatch[1]);
      result.descriptions['efficiency'] = efficiencyMatch[2].trim();
    }

    const bestPracticesMatch = responseText.match(/Best Practices:\s*(\d+)%\s*-\s*([^\n]+)/);
    if (bestPracticesMatch) {
      result.bestPractices = parseInt(bestPracticesMatch[1]);
      result.descriptions['bestPractices'] = bestPracticesMatch[2].trim();
    }

    const maintainabilityMatch = responseText.match(/Maintainability:\s*(\d+)%\s*-\s*([^\n]+)/);
    if (maintainabilityMatch) {
      result.maintainability = parseInt(maintainabilityMatch[1]);
      result.descriptions['maintainability'] = maintainabilityMatch[2].trim();
    }

    const documentationMatch = responseText.match(/Documentation:\s*(\d+)%\s*-\s*([^\n]+)/);
    if (documentationMatch) {
      result.documentation = parseInt(documentationMatch[1]);
      result.descriptions['documentation'] = documentationMatch[2].trim();
    }

    const securityMatch = responseText.match(/Security:\s*(\d+)%\s*-\s*([^\n]+)/);
    if (securityMatch) {
      result.security = parseInt(securityMatch[1]);
      result.descriptions['security'] = securityMatch[2].trim();
    }

    const overallMatch = responseText.match(/Overall Score:\s*(\d+)%/);
    if (overallMatch) {
      result.overall = parseInt(overallMatch[1]);
    }

    // Extract recommendations
    const recommendationsSection = responseText.match(/Recommendations:\s*([\s\S]+?)(?:\n\s*\n|$)/);
    if (recommendationsSection) {
      const recommendations = recommendationsSection[1]
        .split('\n')
        .map(line => line.replace(/^-\s*/, '').trim())
        .filter(line => line.length > 0);
      
      result.recommendations = recommendations;
    }

    return result;
  }
}

export default new CodeReviewService();
